import { html } from 'onefold';
import { CodeBlock } from '../../components/CodeBlock';
import { Callout } from '../../components/Callout';
import { Playground } from '../../components/Playground';

export function ComponentMetaPage(): Node {
  return html`
    <div>
      <h1>Component Metadata</h1>
      <p><code>component()</code> registers components with metadata for dev tools, documentation generation, and design system catalogs.</p>

      <h2>Registering Components</h2>
      ${CodeBlock(`import { html } from 'onefold';
import { component } from 'onefold/meta';

const Button = component({
  name: 'Button',
  description: 'Primary action button',
  props: {
    label: { type: 'string', required: true },
    variant: { type: 'string', default: 'primary' },
    disabled: { type: 'boolean', default: false },
  },
  render: (props) => html\`
    <button class=\${\`btn btn-\${props.variant}\`} disabled=\${props.disabled}>
      \${props.label}
    </button>
  \`,
});`)}

      <h2>getComponentRegistry</h2>
      <p>Retrieve all registered components (useful for dev tools and design systems):</p>
      ${CodeBlock(`import { getComponentRegistry } from 'onefold/meta';

const registry = getComponentRegistry();
// Map<string, ComponentMeta>

for (const [name, meta] of registry) {
  console.log(name, meta.description, meta.props);
}`)}

      <h2>exportManifest</h2>
      <p>Export a JSON manifest of all components for documentation tools:</p>
      ${CodeBlock(`import { exportManifest } from 'onefold/meta';

const manifest = exportManifest();
// {
//   components: [
//     { name: 'Button', description: '...', props: [...] },
//     { name: 'Card', description: '...', props: [...] },
//   ]
// }

// Write to file in a build script
fs.writeFileSync('component-manifest.json', JSON.stringify(manifest, null, 2));`)}

      ${Callout('Component metadata is optional — it does not affect runtime behavior. Use it for tooling, documentation, and design system governance.')}

      <h2>API</h2>
      <table>
        <tr><th>Function</th><th>Parameters</th><th>Description</th></tr>
        <tr><td><code>component</code></td><td>(meta: ComponentMeta)</td><td>Register a component with metadata and render function.</td></tr>
        <tr><td><code>getComponentRegistry</code></td><td>()</td><td>Get all registered components.</td></tr>
        <tr><td><code>exportManifest</code></td><td>()</td><td>Export JSON manifest of all components.</td></tr>
      </table>

      <h2>Try It</h2>
      <p>Register components with metadata and inspect the registry:</p>

      ${Playground(`import { html, mount } from 'onefold';
import { component, getComponentRegistry, exportManifest } from 'onefold/meta';
import { createSignal } from 'onefold';

// Register components with metadata
const Button = component({
  name: 'Button',
  description: 'A reusable button with variants',
  props: {
    label: { type: 'string', required: true, description: 'Button text' },
    variant: { type: 'string', default: 'primary', description: 'primary | outline | danger' },
  },
  tags: ['ui', 'input'],
  render: ({ label, variant }) => {
    const styles = {
      primary: 'background:#4f46e5;color:white;border-color:#4f46e5',
      outline: 'background:transparent;border:1px solid #e5e7eb',
      danger: 'background:#ef4444;color:white;border-color:#ef4444',
    };
    return html\`<button style=\${styles[variant] || styles.primary}>\${label}</button>\`;
  },
});

const Card = component({
  name: 'Card',
  description: 'Content card with title and body',
  props: {
    title: { type: 'string', required: true },
    body: { type: 'string', required: true },
  },
  tags: ['ui', 'layout'],
  render: ({ title, body }) => html\`
    <div style="padding:12px;border:1px solid #e5e7eb;border-radius:8px">
      <h4 style="margin-bottom:4px">\${title}</h4>
      <p style="font-size:13px;color:#666">\${body}</p>
    </div>
  \`,
});

function App() {
  const showManifest = createSignal(false);

  // Get registry info
  const registry = getComponentRegistry();
  const manifest = exportManifest();

  return html\`
    <div>
      <h3>Component Metadata Registry</h3>
      <p style="font-size:13px;color:#666;margin-bottom:16px">
        Registered \${registry.size} component(s). Metadata is used by AI tools, visual builders, and docs generators.
      </p>

      <div style="margin-bottom:16px">
        <p style="font-size:13px;font-weight:600;margin-bottom:8px">Live components:</p>
        <div style="display:flex;gap:8px;margin-bottom:8px">
          \${Button({ label: 'Primary', variant: 'primary' })}
          \${Button({ label: 'Outline', variant: 'outline' })}
          \${Button({ label: 'Danger', variant: 'danger' })}
        </div>
        \${Card({ title: 'Example Card', body: 'This card was rendered from the registered component.' })}
      </div>

      <button onclick=\${() => showManifest.set(v => !v)} style="margin-bottom:8px">
        \${() => showManifest() ? 'Hide' : 'Show'} Exported Manifest
      </button>
      \${() => showManifest() ? html\`
        <pre style="background:#0f172a;color:#a5f3fc;padding:12px;border-radius:8px;font-size:11px;overflow-x:auto;max-height:200px">\${JSON.stringify(manifest, null, 2)}</pre>
      \` : null}
    </div>
  \`;
}

mount(App(), document.getElementById('app'));`, 'component() + exportManifest — Metadata Registry')}
    </div>
  `;
}
